import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog.jsx';
import { Button } from '@/components/ui/button.jsx';
import { cn } from '@/lib/utils.js';

// Confirm dialog for destructive (Lost/Cancelled/Expired) and backward
// stage moves. Reason is optional, but recommended on destructive
// moves so the activity log preserves WHY.

const TERMINAL = new Set(['Lost', 'Cancelled', 'Expired']);

export default function MoveToConfirmDialog({
  open,
  onOpenChange,
  fromStage,
  toStage,
  onConfirm,
}) {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) setReason('');
  }, [open]);

  if (!toStage) return null;
  const isTerminal = TERMINAL.has(toStage);
  const reasonRequired = isTerminal;

  async function handleConfirm() {
    if (reasonRequired && reason.trim().length < 3) return;
    setSubmitting(true);
    try {
      await onConfirm(reason.trim() || null);
      onOpenChange?.(false);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Move to {toStage}?</DialogTitle>
          <DialogDescription>
            {isTerminal ? (
              <>
                Closing this deal as <strong className="text-text">{toStage}</strong>.
                Reason is required for the activity log.
              </>
            ) : (
              <>
                Moving from <strong className="text-text">{fromStage}</strong> to{' '}
                <strong className="text-text">{toStage}</strong>. This is a backward
                move — the activity log records the change either way; a reason is
                optional but helpful.
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        <label className="block">
          <span className="text-xs font-medium uppercase tracking-wide text-text-muted">
            Reason{reasonRequired ? ' *' : ' (optional)'}
          </span>
          <textarea
            autoFocus
            required={reasonRequired}
            rows={2}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={
              isTerminal
                ? 'e.g. client went with incumbent · no appetite from carriers · etc.'
                : 'optional note'
            }
            maxLength={500}
            className={cn(
              'mt-1.5 flex w-full rounded-card border border-border bg-bg px-3 py-2 text-sm text-text',
              'placeholder:text-text-dim',
              'focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-1 focus:ring-offset-bg',
              'resize-none'
            )}
          />
        </label>

        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            onClick={() => onOpenChange?.(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleConfirm}
            disabled={submitting || (reasonRequired && reason.trim().length < 3)}
          >
            {submitting ? 'Moving…' : `Move to ${toStage}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
